/**
 * ManageCampaigns
 *
 * Lists and manages supporter video campaigns within the active namespace.
 * Respects plan and permission constraints and provides edit mode controls.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SERVER_URL } from '../../../../api/core';
import { auth } from '../../../../firebase/firebase';
import BasicLoadingIndicator from '../../../../components/ui/feedback/basic-loading-indicator';
import ManageCampaignHeader from '../../../../components/testimonials/manageCampaigns/ManageCampaignHeader';
import CampaignList from '../../../../components/testimonials/manageCampaigns/CampaignList';
import { useNamespace } from '../../../../context/namespace';
import { useAuth } from '../../../../context/auth';
import Footer from '../../../../components/shared/Footer';
import { Shield, Crown, ChevronRight } from 'lucide-react';
import { getCampaigns, deleteCampaign, updateCampaign } from '../../../../api/campaigns/campaignsApi';
import EmptyCampaignState from '../../../../components/testimonials/manageCampaigns/EmptyCampaignState';
import SEOHead from '../../../../components/shared/SEOHead';

export default function ManageCampaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Get the current namespace from context
  const { 
    currentNamespace, 
    namespaces: contextNamespaces, // Use context namespaces
    userPermission, 
    isLoading: namespacesLoading, // Use context loading state
    // fetchUserNamespaces // Removed fetchUserNamespaces import if not used elsewhere
  } = useNamespace();
  
  // Get user data from auth context
  const { user, fetchUserProfile } = useAuth();
  
  // Check if user has premium subscription
  const isPremium = user?.planLevel === 'premium' || user?.subscriptionStatus?.currentPlanLevel === 'premium';
  
  // Clear any errors for non-premium users to avoid showing "Forbidden" errors
  useEffect(() => {
    if (!isPremium && error) {
      setError(null);
    }
  }, [isPremium, error]);
  
  // Check if user has read-only permission
  const hasReadOnlyPermission = userPermission === 'readonly' || userPermission === 'read-only';
  
  // Debug permissions
  console.log('ManageCampaigns permission check:', {
    userPermission,
    hasReadOnlyPermission,
    isPremium
  });
  
  // Find the current namespace ID
  const currentNamespaceObj = contextNamespaces.find(ns => ns.name === currentNamespace);
  const currentNamespaceId = currentNamespaceObj?.id;

  // Exit edit mode if user has read-only permission
  useEffect(() => {
    if (hasReadOnlyPermission && isEditMode) {
      setIsEditMode(false);
    }
  }, [hasReadOnlyPermission, isEditMode]);

  // Delay showing errors to prevent flashing
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setShowError(true);
      }, 500); // Delay showing error by 500ms
      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [error]);

  // Mark when initial load is complete
  useEffect(() => {
    if (!namespacesLoading && !isLoading) {
      setInitialLoadComplete(true);
    }
  }, [namespacesLoading, isLoading]);

  useEffect(() => {
    console.log('Campaign fetch useEffect triggered. State:', {
      namespacesLoading,
      currentNamespaceId,
      isPremium,
      contextNamespacesLength: contextNamespaces.length
    });

    // Only proceed if we're not still loading namespaces
    if (!namespacesLoading) {
      console.log('Namespaces are loaded.');
      if (currentNamespaceId) {
        console.log('Current namespace ID found:', currentNamespaceId);
        // Only fetch campaigns if user has premium subscription
        if (!isPremium) {
          // Don't make API call for non-premium users
          setIsLoading(false);
          console.log('User is not premium. Skipping campaign fetch.');
          return;
        }

        // Fetch campaigns directly if conditions are met
        console.log('User is premium. Attempting to fetch campaigns for namespace:', currentNamespaceId);
        fetchCampaigns();

      } else {
        console.log('No currentNamespaceId found.');
        if (contextNamespaces.length > 0) {
          // Only show error if namespaces are loaded but none is selected
          setError('No namespace selected. Please select a namespace to view campaigns.');
          setIsLoading(false);
          console.log('Error set: No namespace selected.');
        } else { // contextNamespaces.length === 0
          // If no namespaces exist at all
          setError('No namespaces available. Please create a namespace first.');
          setIsLoading(false);
          console.log('Error set: No namespaces available.');
        }
      }
    } else {
      console.log('Namespaces are still loading.');
    }
  }, [currentNamespaceId, namespacesLoading, contextNamespaces, isPremium]);

  const fetchCampaigns = async () => {
    console.log('fetchCampaigns called. currentNamespaceId:', currentNamespaceId); // Log entry point
    if (!currentNamespaceId) {
      console.log('fetchCampaigns aborted: No currentNamespaceId.');
      return; // Ensure it returns if ID is missing
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use the API method from campaignsApi.js
      console.log(`Calling getCampaigns(${currentNamespaceId})...`); // Log before API call
      const data = await getCampaigns(currentNamespaceId);
      console.log('getCampaigns returned:', data); // Log API response
      // API returns null on 404 (e.g., namespace not found) — normalize to empty list
      setCampaigns(Array.isArray(data) ? data : []);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error in fetchCampaigns:', err); // Log errors specifically from fetchCampaigns
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (campaignId) => {
    try {
      // Use the API method from campaignsApi.js
      await deleteCampaign(currentNamespaceId, campaignId);
      
      // Update the UI by filtering out the deleted campaign
      setCampaigns((prev) => prev.filter((c) => c.id !== campaignId));
      setSuccessMessage('Campaign deleted successfully');
      
      // Update the user profile without triggering a full campaigns refresh
      if (auth.currentUser) {
        console.log('Refreshing profile data to update campaign counts after deletion');
        // Pass a flag to prevent cascading refreshes in the fetchUserProfile function
        await fetchUserProfile(auth.currentUser, true, { skipCampaignRefresh: true });
      }
      
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error deleting campaign:', err);
      setError(err.message);
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  };

  const handleUpdate = async (campaignId, updatedData) => {
    try {
      // Use the API method from campaignsApi.js
      const updatedCampaign = await updateCampaign(currentNamespaceId, campaignId, updatedData);
      
      // Update the UI with the updated campaign data
      setCampaigns((prev) =>
        prev.map((c) => (c.id === campaignId ? { ...c, ...updatedCampaign } : c))
      );
      setSuccessMessage('Campaign updated successfully');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return true;
    } catch (err) {
      console.error('Error updating campaign:', err);
      setError(err.message);
      setTimeout(() => {
        setError(null);
      }, 5000);
      return false;
    }
  };

  const handleToggleEdit = () => {
    // Prevent entering edit mode for users with read-only access
    if (hasReadOnlyPermission) {
      setError("You don't have permission to edit campaigns");
      setTimeout(() => {
        setError(null);
      }, 5000);
      return;
    }
    
    setIsEditMode(!isEditMode);
  };

  const handleNewCampaign = () => {
    // Check if user has premium plan before allowing to create new campaign
    if (!isPremium) {
      navigate('/app/account', { 
        state: { 
          fromFeature: true,
          returnPath: '/app/campaigns',
          featureName: 'Campaign Creation'
        } 
      });
      return;
    }
    
    // If premium, proceed to campaign creation
    navigate('/app/campaigns/new');
  };

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      <SEOHead pageType="app-campaigns" />
      {/* Premium feature notification */}
      {!isPremium && (
        <div className="mb-6 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800/50 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Crown className="h-5 w-5 text-purple-500 dark:text-purple-400 mr-3" />
              <p className="text-sm text-purple-800 dark:text-purple-300">
                Campaign creation is a premium feature. Upgrade to create and manage multiple campaigns.
              </p>
            </div>
            <button 
              onClick={() => navigate('/app/account', { state: { fromFeature: true, returnPath: '/app/campaigns', featureName: 'Campaign Creation' }})}
              className="ml-4 flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-md text-xs font-medium"
            >
              Upgrade
              <ChevronRight className="ml-1 h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {successMessage}
        </div>
      )}
      
      {/* Namespace warning - only show after initial load and with a delay */}
      {!currentNamespaceId && !namespacesLoading && contextNamespaces.length > 0 && initialLoadComplete && showError && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-900/50">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400 dark:text-amber-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">Namespace Required</h3>
              <div className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                <p>Please select a namespace to view and manage campaigns. Campaigns are shared within a namespace.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {error && showError && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {error}
        </div>
      )}

      {/* Read-only access notification */}
      {hasReadOnlyPermission && currentNamespaceId && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-gray-700 dark:bg-gray-800/90">
          <div className="flex items-start">
            <div className="mr-3 flex-shrink-0">
              <Shield className="h-5 w-5 text-amber-600 dark:text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-400">Read-Only Access</h3>
              <div className="mt-1 text-sm text-amber-700 dark:text-gray-300">
                <p>You currently have read-only access to this namespace. Contact an administrator to request edit permissions.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <ManageCampaignHeader
        isEditMode={isEditMode}
        onToggleEdit={handleToggleEdit}
        onNewCampaign={handleNewCampaign}
        hasCampaigns={!isLoading && campaigns.length > 0}
        currentNamespace={currentNamespace}
        userPermission={userPermission}
        hasReadOnlyPermission={hasReadOnlyPermission}
      />

      <div className="mt-4">
        {namespacesLoading || isLoading ? (
          <BasicLoadingIndicator />
        ) : !isPremium ? (
          /* Show simplified EmptyCampaignState for non-premium users */
          <EmptyCampaignState onNewCampaign={handleNewCampaign} hasReadOnlyPermission={false} showPremiumBanner={false} />
        ) : campaigns.length === 0 ? (
          /* Show simplified EmptyCampaignState for premium users with no campaigns */
          <EmptyCampaignState onNewCampaign={handleNewCampaign} hasReadOnlyPermission={hasReadOnlyPermission} showPremiumBanner={false} />
        ) : (
          /* Show campaign list for premium users with campaigns */
          <CampaignList
            isLoading={isLoading}
            campaigns={campaigns}
            isEditMode={isEditMode}
            onDelete={handleDelete}
            onUpdate={handleUpdate}
            onCampaignClick={(id) => navigate(`/app/campaigns/${id}`)}
            onNewCampaign={handleNewCampaign}
            currentNamespaceId={currentNamespaceId}
            hasReadOnlyPermission={hasReadOnlyPermission}
          />
        )}
      </div>

      <Footer />
    </div>
  );
}
